import { RenderFieldParams, useDynamicFormField } from '@ui-common/hooks/useDynamicFormField'
import { YesNo } from '@ui-common/types'
import { MouseEvent, useCallback, useEffect, useLayoutEffect } from 'react'
import {
  ArrayPath,
  Control,
  FieldArray,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormWatch,
  useWatch,
} from 'react-hook-form'

type Applicant<
  TFieldValues extends FieldValues,
  TDataProp extends Path<TFieldValues>,
  THasProp extends Path<TFieldValues>,
  TApplicantFields extends Record<string, unknown>,
> = {
  hasProp: THasProp
  dataProp: TDataProp
} & TApplicantFields

type RenderableApplicant<
  TFieldValues extends FieldValues,
  TDataProp extends Path<TFieldValues>,
  THasProp extends Path<TFieldValues>,
  TApplicantFields extends Record<string, unknown>,
> = {
  renderFields: RenderFieldParams<TFieldValues>[]
  remove: (event: MouseEvent<HTMLButtonElement>, index: number) => void
  append: () => void
  data: NonNullable<PathValue<TFieldValues, TDataProp>>
  has: YesNo
} & Applicant<TFieldValues, TDataProp, THasProp, TApplicantFields>

type EntryGenerator<T extends FieldValues> = () => FieldArray<T, ArrayPath<T>>

/**
 * Custom hook for generating toggleable form arrays that can be dynamically added or removed by the user
 *
 * @param applicant - Object that contains names of the YesNo property in the form data as well as the name of the property of the data itself in the form data
 * @param control - control function you get from useWizardForm hook
 * @param watch - watch function you get from useWizardForm hook
 * @param register - register function you get from useWizardForm hook
 * @param initialLengthForFocusFix - When appending a dynamic form field, and the first focused input is unsupported by React Hook Form (e.g. radio), pass this to enable the fix
 * @param entryGenerator - Function that generates new empty entries
 */
export const useToggleableDynamicFormField = <
  TFieldValues extends FieldValues,
  TDataProp extends Path<TFieldValues>,
  THasProp extends Path<TFieldValues>,
  TApplicantFields extends Record<string, unknown>,
>(
  applicant: Applicant<TFieldValues, TDataProp, THasProp, TApplicantFields>,
  control: Control<TFieldValues>,
  watch: UseFormWatch<TFieldValues>,
  register: UseFormRegister<TFieldValues>,
  initialLengthForFocusFix: number,
  entryGenerator?: EntryGenerator<TFieldValues>,
  optimize?: boolean,
): RenderableApplicant<TFieldValues, TDataProp, THasProp, TApplicantFields> => {
  const { dataProp, hasProp } = applicant
  const { renderFields, removeField, appendField } = useDynamicFormField({
    name: dataProp as ArrayPath<TFieldValues>,
    control,
    watch,
    shouldUnregisterFields: true,
    initialLengthForFocusFix,
    optimize,
  })

  const has = useWatch({ name: hasProp, control })
  const data = useWatch({ name: dataProp, control })
  const append = useCallback(
    (shouldFocus?: boolean) => {
      const defaultEntry = {} as FieldArray<TFieldValues, ArrayPath<TFieldValues>>
      const entry = entryGenerator?.() ?? defaultEntry
      appendField(entry, {
        focusIndex: 0,
        shouldFocus,
      })
    },
    [appendField, entryGenerator],
  )

  useLayoutEffect(() => {
    if (has === YesNo.Yes && (!renderFields || renderFields?.length === 0)) {
      append(false)
    }
  }, [append, renderFields, has])

  useEffect(() => {
    register(hasProp)
  }, [hasProp, register])

  return {
    ...applicant,
    renderFields,
    has,
    data,
    append,
    remove: removeField,
  }
}
